import { Button } from "@client/components/utils/Button";
import { DispatchContext, StateContext } from "@client/pages/round/Round";
import { DownloadFile } from "@client/utils/DownloadFile";
import { exportScores } from "@client/utils/exportScores";
import { importScores } from "@client/utils/importScores";
import { getActiveRecommenders, getProposal } from "@engine/round/accessors";
import { Funder, RoundAggregate } from "@engine/round/aggregate";
import { uniq } from "lodash";
import { useContext, useMemo, useRef, useState } from "react";
import {
  DataGrid,
  SortColumn,
  textEditor,
  type Column,
  type DataGridHandle,
} from "react-data-grid";
import "react-data-grid/lib/styles.css";
import { toast } from "react-toastify";

export type DataRow = {
  orgName: string;
  evaluatorName: string;
  rating: string;
  proposalId: string;
  funderId: string;
  recommenderId: string;
};

export function getRatingRows(
  round: RoundAggregate,
  funder: Funder,
): DataRow[] {
  const rows: DataRow[] = [];

  const recommenders = getActiveRecommenders(round);

  for (const recommender of recommenders) {
    if (recommender.shortname.startsWith("Mean")) {
      continue;
    }

    const member = round.members[recommender.memberId];
    if (!member) {
      toast.warn(`Member not found for recommender ${recommender.shortname}`);
      continue;
    }

    const notedProposalIds = Object.entries(member.evaluableNotes)
      .filter(([_, note]) => !!note)
      .map(([proposalId]) => proposalId);

    const feedbackProposalIds = Object.entries(member.evaluableFeedback)
      .filter(
        ([, feedback]) =>
          feedback?.positiveNegative.negative !== undefined ||
          feedback?.positiveNegative.positive !== undefined,
      )
      .map(([proposalId]) => proposalId);

    const maybeRatedProposalIds = uniq([
      ...notedProposalIds,
      ...feedbackProposalIds,
    ]);

    for (const proposalId of maybeRatedProposalIds) {
      const proposal = getProposal(round, proposalId);
      if (!proposal) {
        toast.warn(`Proposal not found for proposalId ${proposalId}`);
        continue;
      }

      const score = funder.scores[recommender.id]?.[proposalId];

      const row: DataRow = {
        orgName: proposal.shortname,
        evaluatorName: recommender.shortname,
        rating: score != null ? score.toString() : "",
        proposalId,
        funderId: funder.id,
        recommenderId: recommender.id,
      };

      rows.push(row);
    }
  }

  rows.sort((a, b) =>
    a.orgName.toLowerCase().localeCompare(b.orgName.toLowerCase()),
  );

  return rows;
}

export function RatingTable({ funder }: { funder: Funder }) {
  const gridRef = useRef<DataGridHandle | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { round } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const [rows, setRows] = useState<DataRow[]>(() =>
    getRatingRows(round, funder),
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);

  const columns: Column<DataRow>[] = [
    { key: "orgName", name: "Org Name", sortable: true },
    { key: "evaluatorName", name: "Evaluator Name", sortable: true },
    { key: "rating", name: "Rating", renderEditCell: textEditor },
  ];

  const onRowsChange = (newRows: DataRow[]) => {
    setRows(newRows);
    setHasChanges(true);
  };

  async function submitUpdateScores() {
    const scoresRows: {
      recommenderId: string;
      proposalId: string;
      score?: number;
    }[] = rows.map((row) => {
      const parsedScore = parseFloat(row.rating);

      return {
        recommenderId: row.recommenderId,
        proposalId: row.proposalId,
        score: isNaN(parsedScore) ? undefined : parsedScore,
      };
    });

    // TODO hasChanges doesn't have a "save in progress state"; we void this rather than await so the button disappears immediately, but we should wait until it finishes before releasing the are-you-sure-you-want-to-leave-the-tab lock
    void dispatch({
      type: "FUNDER_SCORES_CHANGE",
      funderId: funder.id,
      scores: scoresRows,
    });
    setHasChanges(false);
  }

  function handleCellKeyDown(args, event) {
    const { column, rowIdx } = args;

    if (event.key === "Enter" && gridRef.current) {
      const currentValue = args.row[column.key];

      const isValidRating = (value: string) => {
        const numValue = Number(value);
        return !isNaN(numValue) && numValue >= 0 && numValue <= 5;
      };

      if (column.key === "rating" && !isValidRating(currentValue)) {
        event.preventDefault();
        toast.error("Please enter a rating between 0 and 5.");
        return;
      }

      const colIdx = column.idx;
      event.stopPropagation();
      gridRef.current.selectCell(
        {
          idx: colIdx,
          rowIdx: rowIdx + 1,
        },
        {},
      );
    }
  }

  // useEffect(() => {
  //   if (!hasChanges) {
  //     return;
  //   }

  //   const unblock = session.block((update) => {
  //     if (
  //       confirm("You have unsaved changes. Are you sure you want to leave?")
  //     ) {
  //       unblock();
  //       update.retry();
  //     }
  //   });

  //   return () => {
  //     unblock();
  //   };
  // }, [hasChanges]);

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          setRows((currentRows) => {
            console.log("Importing new data...");
            const updatedRows = importScores(content, currentRows);
            setHasChanges(true);
            return updatedRows;
          });
        }
      };
      reader.readAsText(file);

      event.target.value = "";
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return rows;

    return [...rows].sort((a, b) => {
      for (const { columnKey, direction } of sortColumns) {
        const aVal = a[columnKey];
        const bVal = b[columnKey];

        if (aVal === bVal) continue;

        if (direction === "ASC") {
          return aVal < bVal ? -1 : 1;
        } else {
          return aVal > bVal ? -1 : 1;
        }
      }

      return 0;
    });
  }, [rows, sortColumns]);

  const recommenders = getActiveRecommenders(round);

  return (
    <>
      <DataGrid
        className="h-[80vh] rdg-light dark:rdg-dark"
        ref={gridRef}
        columns={columns}
        sortColumns={sortColumns}
        onSortColumnsChange={setSortColumns}
        rows={sortedRows}
        onRowsChange={onRowsChange}
        onCellKeyDown={handleCellKeyDown}
      />
      <div className="flex justify-between mt-2 mx-2">
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImport}
            accept=".tsv"
            className="hidden"
          />
          <Button onClick={triggerFileInput}>Import</Button>
          <DownloadFile
            data={exportScores(
              rows,
              recommenders
                .map((r) => r.shortname)
                .filter((r) => !r.startsWith("Mean"))
                .sort(),
            )}
            filename="scores.tsv"
            className="p-2 rounded bg-stone-400 hover:bg-stone-500 dark:bg-stone-700 dark:hover:bg-stone-800"
            title="Export"
          />
        </div>
        <div>
          {hasChanges && (
            <button
              onClick={submitUpdateScores}
              className="p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
            >
              Save Changes
            </button>
          )}
        </div>
      </div>
    </>
  );
}
